import { trackEvent } from 'arts/config/analytics';
import Constants from '../config/const';
import { loadWorkDetails } from './works';
import { redo } from './undo';
import { saveWall } from './save_wall';

const detectBestPosition = (width, height, works, centerX, centerY) => {
  let posX = centerX - width / 2;
  let posY = centerY - height / 2;
  const work = works.find(({ x, y }) => (x === posX && y === posY));
  if (work) {
    posX += 1;
    posY += 1;
  }
  return {
    x: posX,
    y: posY
  };
};

export const addToCanvas = ({
  workId, workSizeId, frameId, width, height
}) => (dispatch, getState) => {
  const { silhouetteX, silhouetteY, works } = getState().canvas;
  if (!getState().workDetails[workId]) {
    dispatch(loadWorkDetails(workId));
  }
  const silhouetteCenterX = silhouetteX + (Constants.silhouette.width / 2);
  const silhouetteCenterY = silhouetteY + (Constants.silhouette.height / 2);
  const { x, y } = detectBestPosition(width, height, works, silhouetteCenterX, silhouetteCenterY);
  dispatch(redo({
    type: Constants.canvas.remove,
    index: works.length
  }));
  dispatch({
    type: Constants.canvas.add,
    workId,
    workSizeId,
    frameId,
    x,
    y
  });
  dispatch(saveWall());
  const { wallId, currency } = window.wall;
  trackEvent('wall builder: add work', {
    'wall id': wallId,
    currency,
    'work id': workId,
    'frame id': frameId,
    width,
    height
  });
};

export const removeFromCanvas = (index) => (dispatch, getState) => {
  const work = getState().canvas.works[index];
  const { workId, frameId } = work;
  // Undo has to put this exact object back, not an equivalent one: the save below destroys
  // the row, so `sourceWallId` only survives on the canvas, and the server re-tags the
  // recreated row from it. `key` is what the save response maps the new row id back onto.
  dispatch(redo({
    type: Constants.canvas.restore,
    index,
    work
  }));
  dispatch({
    type: Constants.canvas.remove,
    index
  });
  dispatch(saveWall());
  const { wallId, currency } = window.wall;
  trackEvent('wall builder: remove work', {
    'wall id': wallId,
    currency,
    'work id': workId,
    'frame id': frameId
  });
};

export const moveWorkTo = ({ index, x, y }) => (dispatch) => {
  dispatch({
    type: Constants.canvas.move,
    index,
    x,
    y
  });
  dispatch(saveWall());
};

export const zoomIn = () => ({
  type: Constants.canvas.zoomIn
});

export const zoomOut = () => ({
  type: Constants.canvas.zoomOut
});

export const resetZoom = () => ({
  type: Constants.canvas.resetZoom
});

export const recalculateDpis = () => ({
  type: Constants.canvas.recalculateDpis
});

export const resetCenter = (canvasWidth, canvasHeight) => ({
  type: Constants.canvas.resetCenter,
  canvasWidth,
  canvasHeight
});

export const setWorkSize = (workSizeId, index) => (dispatch, getState) => {
  const { workSizeId: prevWorkSizeId } = getState().canvas.works[index];
  dispatch(redo({
    type: Constants.canvas.setSize,
    workSizeId: prevWorkSizeId,
    index
  }));
  dispatch({
    type: Constants.canvas.setSize,
    workSizeId,
    index
  });
  dispatch(saveWall());
};

export const setNonGlare = (nonGlare, index) => (dispatch, getState) => {
  const { nonGlare: prevNonGlare } = getState().canvas.works[index];
  dispatch(redo({
    type: Constants.canvas.setNonGlare,
    nonGlare: prevNonGlare,
    index
  }));
  dispatch({
    type: Constants.canvas.setNonGlare,
    nonGlare,
    index
  });
  dispatch(saveWall());
};

export const setFrame = (frameId, index) => (dispatch, getState) => {
  const { frameId: prevFrameId } = getState().canvas.works[index];
  dispatch(redo({
    type: Constants.canvas.setFrame,
    frameId: prevFrameId,
    index
  }));
  dispatch({
    type: Constants.canvas.setFrame,
    frameId,
    index
  });
  dispatch(saveWall());
};

export const setCenter = (centerX, centerY, x, y) => ({
  type: Constants.canvas.setCenter,
  centerX,
  centerY,
  posX: x,
  posY: y
});

export const setColor = (currentColor) => (dispatch, getState) => {
  const { currentColor: prevColor } = getState().canvas;
  dispatch(redo({
    type: Constants.canvas.setColor,
    currentColor: prevColor
  }));
  dispatch({
    type: Constants.canvas.setColor,
    currentColor
  });
  dispatch(saveWall());
};

export const showRuler = () => ({
  type: Constants.canvas.showRuler
});

export const hideRuler = () => ({
  type: Constants.canvas.hideRuler
});

export const showGuides = () => ({
  type: Constants.canvas.showGuides
});

export const hideGuides = () => ({
  type: Constants.canvas.hideGuides
});

export const showWidth = () => ({
  type: Constants.canvas.showWidth
});

export const hideWidth = () => ({
  type: Constants.canvas.hideWidth
});

export const setWidth = (width) => (dispatch) => {
  dispatch({
    type: Constants.canvas.setWidth,
    width
  });
  dispatch(saveWall());
};

export const showHeight = () => ({
  type: Constants.canvas.showHeight
});

export const hideHeight = () => ({
  type: Constants.canvas.hideHeight
});

export const setHeight = (height) => (dispatch) => {
  dispatch({
    type: Constants.canvas.setHeight,
    height
  });
  dispatch(saveWall());
};

export const updateAttributes = (attributes) => (dispatch) => {
  dispatch({
    type: Constants.canvas.updateAttributes,
    attributes
  });
  dispatch(saveWall());
};

export const setSilhouette = (silhouette) => ({
  type: Constants.canvas.setSilhouette,
  silhouette
});

export const hideSilhouette = () => ({
  type: Constants.canvas.hideSilhouette
});
